import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    { icon: Phone, title: t("contact.phone"), value: "+371 29173338", name: "Agnese Bērziņa", href: "tel:+37129173338" },
    { icon: Phone, title: t("contact.phone"), value: "+371 25775533", name: "Didzis Tupureins", href: "tel:+37125775533" },
    { icon: Mail, title: t("contact.email"), value: "info@drosavide.lv", href: "mailto:info@drosavide.lv" },
    { icon: MapPin, title: t("contact.location"), value: t("contact.locationValue"), href: null }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(t("contact.errorMsg"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakti" className="section-spacing contact-section" data-testid="contact-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div className="text-white">
            <span className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">{t("contact.badge")}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("contact.title")}</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">{t("contact.description")}</p>

            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <div key={index} className="flex items-center gap-4" data-testid={`contact-info-${index + 1}`}>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-white/70">{item.title}</p>
                    {item.href ? (
                      <a href={item.href} className="text-lg text-white font-medium hover:underline">{item.value}</a>
                    ) : (
                      <p className="text-lg text-white font-medium">{item.value}</p>
                    )}
                    {item.name && <p className="text-sm text-white/70 mt-0.5">{item.name}</p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 bg-white/10 rounded-2xl">
              <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("contact.companyName")}</h3>
              <p className="text-white/80">{t("contact.companySub")}<br />{t("contact.companyDesc")}</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl">
            {isSubmitted ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("contact.successTitle")}</h3>
                <p className="text-muted-foreground mb-6">{t("contact.successDesc")}</p>
                <Button onClick={() => setIsSubmitted(false)} variant="outline" className="rounded-full">{t("contact.successBtn")}</Button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("contact.formTitle")}</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-base font-medium">{t("contact.labelName")}</Label>
                    <Input id="name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder={t("contact.placeholderName")} required className="input-field" data-testid="contact-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base font-medium">{t("contact.labelEmail")}</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder={t("contact.placeholderEmail")} required className="input-field" data-testid="contact-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-base font-medium">{t("contact.labelPhone")}</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder={t("contact.placeholderPhone")} required className="input-field" data-testid="contact-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-base font-medium">{t("contact.labelMessage")}</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder={t("contact.placeholderMessage")} required rows={4} className="bg-muted border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl text-lg px-4 py-3 resize-none" data-testid="contact-message" />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="btn-primary w-full group" data-testid="contact-submit">
                    {isSubmitting ? t("contact.submitting") : (<>{t("contact.submit")}<Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
                  </Button>
                </form>
                <p className="text-sm text-muted-foreground text-center mt-6">{t("contact.privacyNote")}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
